import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Trip } from '../models/trip';
import { StorageService } from './storage.service';

export const TRIP_CACHE_STORAGE_KEY = 'trip-report-cache-v1';

const TRIP_CACHE_VERSION = 1;

interface CachedRange {
  from: string;
  to: string;
}

interface StoredTrip extends Omit<Trip, 'startTime' | 'endTime'> {
  startTime?: string;
  endTime?: string;
}

interface DeviceTripCache {
  ranges: CachedRange[];
  trips: StoredTrip[];
  updatedAt: string;
}

interface TripCacheStore {
  version: number;
  devices: Record<string, DeviceTripCache>;
}

@Injectable({
  providedIn: 'root',
})
export class TripCacheService {
  constructor(private storage: StorageService) {}

  get(deviceId: number, from: Date, to: Date): Observable<Trip[] | null> {
    const fromTime = from.getTime();
    const toTime = to.getTime();

    return this.storage.fetch<TripCacheStore>(TRIP_CACHE_STORAGE_KEY).pipe(
      map((store) => {
        const deviceCache = this.getDeviceCache(store, deviceId);
        if (!deviceCache || !this.isRangeCovered(deviceCache.ranges, fromTime, toTime)) return null;

        return deviceCache.trips
          .filter((trip) => this.isTripWithinRange(trip, fromTime, toTime))
          .map((trip) => this.deserializeTrip(trip))
          .sort((first, second) => new Date(second.startTime).getTime() - new Date(first.startTime).getTime());
      }),
    );
  }

  store(deviceId: number, from: Date, to: Date, trips: Trip[]): Observable<void> {
    const fromTime = from.getTime();
    const toTime = to.getTime();

    return this.storage.fetch<TripCacheStore>(TRIP_CACHE_STORAGE_KEY).pipe(
      map((cachedStore) => this.getStore(cachedStore)),
      map((store) => {
        const deviceKey = String(deviceId);
        const current = store.devices[deviceKey] || { ranges: [], trips: [], updatedAt: '' };
        const tripsOutsideRange = current.trips.filter((trip) => !this.isTripWithinRange(trip, fromTime, toTime));
        const tripsInsideRange = trips
          .filter((trip) => this.isTripWithinRange(trip, fromTime, toTime))
          .map((trip) => this.serializeTrip(trip));

        return {
          ...store,
          devices: {
            ...store.devices,
            [deviceKey]: {
              ranges: this.mergeRanges([...current.ranges, { from: from.toISOString(), to: to.toISOString() }]),
              trips: this.deduplicateTrips([...tripsOutsideRange, ...tripsInsideRange]),
              updatedAt: new Date().toISOString(),
            },
          },
        };
      }),
      switchMap((store) => this.storage.store(TRIP_CACHE_STORAGE_KEY, store)),
    );
  }

  clear(): Observable<void> {
    return this.storage.delete(TRIP_CACHE_STORAGE_KEY);
  }

  private getStore(store: TripCacheStore | null): TripCacheStore {
    if (store?.version === TRIP_CACHE_VERSION && store.devices) return store;
    return { version: TRIP_CACHE_VERSION, devices: {} };
  }

  private getDeviceCache(store: TripCacheStore | null, deviceId: number): DeviceTripCache | null {
    if (store?.version !== TRIP_CACHE_VERSION || !store.devices) return null;
    return store.devices[String(deviceId)] || null;
  }

  private isRangeCovered(ranges: CachedRange[], from: number, to: number): boolean {
    return this.mergeRanges(ranges).some(
      (range) => new Date(range.from).getTime() <= from && new Date(range.to).getTime() >= to,
    );
  }

  private mergeRanges(ranges: CachedRange[]): CachedRange[] {
    const sortedRanges = ranges
      .map((range) => ({ from: new Date(range.from).getTime(), to: new Date(range.to).getTime() }))
      .filter((range) => Number.isFinite(range.from) && Number.isFinite(range.to) && range.from < range.to)
      .sort((first, second) => first.from - second.from);

    const mergedRanges: Array<{ from: number; to: number }> = [];
    sortedRanges.forEach((range) => {
      const previous = mergedRanges[mergedRanges.length - 1];
      if (!previous || range.from > previous.to) {
        mergedRanges.push({ ...range });
      } else {
        previous.to = Math.max(previous.to, range.to);
      }
    });

    return mergedRanges.map((range) => ({
      from: new Date(range.from).toISOString(),
      to: new Date(range.to).toISOString(),
    }));
  }

  private isTripWithinRange(trip: Trip | StoredTrip, from: number, to: number): boolean {
    const startTime = new Date(trip.startTime).getTime();
    return Number.isFinite(startTime) && startTime >= from && startTime < to;
  }

  private serializeTrip(trip: Trip): StoredTrip {
    return {
      ...trip,
      startTime: trip.startTime ? new Date(trip.startTime).toISOString() : undefined,
      endTime: trip.endTime ? new Date(trip.endTime).toISOString() : undefined,
    };
  }

  private deserializeTrip(trip: StoredTrip): Trip {
    return {
      ...trip,
      startTime: trip.startTime ? new Date(trip.startTime) : undefined,
      endTime: trip.endTime ? new Date(trip.endTime) : undefined,
    };
  }

  private deduplicateTrips(trips: StoredTrip[]): StoredTrip[] {
    const tripsByIdentity = new Map<string, StoredTrip>();
    trips.forEach((trip) => tripsByIdentity.set(this.getTripIdentity(trip), trip));
    return Array.from(tripsByIdentity.values());
  }

  private getTripIdentity(trip: StoredTrip): string {
    if (trip.startPositionId !== null && trip.startPositionId !== undefined) {
      return `${trip.deviceId}:${trip.startPositionId}`;
    }
    return `${trip.deviceId}:${trip.startTime}:${trip.endTime}`;
  }
}
